/**
 * Roll `/insights/stages` up into one row per stage.
 *
 * The endpoint returns per-(day, stage, prompt_version_id) rows — for a 30-day
 * window over ~20 stages with several live prompt versions that is hundreds of
 * rows, which is a table nobody reads. What an operator wants is "which stage is
 * failing, and is it slow", so this collapses the day and prompt-version axes.
 *
 * `/insights/stages` is **not** class-filtered: it aggregates `stage_executions`
 * directly, so enrichment stages are already in the payload. That is the whole
 * reason this surface can see an incident the scrape-only home KPIs cannot.
 *
 * ## Failure means failure
 *
 * This module used to publish `successRate = okCount / executions`. A stage
 * execution has eight statuses and only `failed`/`failed_retryable` are
 * failures — `ok`, `inherited`, `skipped`, `duplicate`, `hitl` and `deferred`
 * are all normal outcomes. Measured on prod over the seven days to 2026-08-28,
 * that ratio reported:
 *
 * - `movie_materialization` and `person_resolution` at **0.3%** — 99.6% of their
 *   executions are `skipped`, and **zero** failed;
 * - `venue_resolution` at **20.0%** — 79.7% `inherited`, the cheap path that
 *   reuses a sibling event's resolution instead of paying Google Places, and a
 *   0.04% failure rate;
 * - `screening_dedup` at **41.2%** — 56.7% `duplicate`, which is the entire
 *   purpose of the stage.
 *
 * The honest headline is `failRate`. The old number survives as `okRate`, which
 * is a fine thing to want — it was just never "success". `statusMix` carries the
 * rest, so a reader sees *why* a stage is not `ok`: `venue_resolution` reads
 * "0% failed, 80% inherited", not "20% success".
 *
 * Tina publishes the per-status counts only from migration `0057`. They are
 * optional on the wire so this page renders correctly against an older API: the
 * headline `failRate` needs only `fail_count`/`executions`, which have always
 * been there, and `otherCount` is derived by subtraction either way.
 *
 * ## A failure a retry fixed is not a failure
 *
 * `fail_count` counts every failed ATTEMPT, so a stage whose first try often
 * fails and whose retry always works read as broken: over the seven days to
 * 2026-09-23 `performance_dedup` showed 8.70% failed, red, and every one of its
 * 971 failures had been retried to success. Tina migration `0077` publishes
 * `recovered_fail_count`, the part of `fail_count` a later attempt fixed. By
 * default `failCount` leaves it out; `includeRecovered` puts it back. Against
 * an older API the field is absent and reads as 0, i.e. the old behaviour.
 */
import { isEnrichmentStage } from "@/lib/tina/spend-class";
import type { StagePerfDailyRow } from "@/types/tina";

export type StageClass = "scrape" | "enrichment";

/** The five non-`ok`, non-failure outcomes, in the order the UI lists them. */
export const NON_OK_STATUSES = [
  "inherited",
  "skipped",
  "duplicate",
  "hitl",
  "deferred",
] as const;

export type NonOkStatus = (typeof NON_OK_STATUSES)[number];

export type StatusMix = Record<NonOkStatus, number>;

export interface StageHealthRow {
  stage: string;
  stageClass: StageClass;
  executions: number;
  okCount: number;
  /**
   * `failed` + `failed_retryable` attempts, minus the ones a later retry fixed
   * unless `includeRecovered` was set.
   */
  failCount: number;
  /** Failed attempts a later retry fixed — counted whether shown or not. */
  recoveredCount: number;
  /**
   * The headline: `failCount / executions`, where a failure is `failed` or
   * `failed_retryable` and nothing else. Null when the stage ran zero times.
   */
  failRate: number | null;
  /**
   * `okCount / executions`. This is the field that used to be called
   * `successRate`; it is not a success rate, because five of the six non-`ok`
   * statuses are intended outcomes. Kept because "how often did this stage
   * return a fresh result" is a real question — just not a health question.
   */
  okRate: number | null;
  /**
   * Executions that were neither `ok` nor a failure — always derivable, even
   * from a pre-`0057` payload that carries no per-status counts.
   */
  otherCount: number;
  /**
   * Per-status counts for `otherCount`. Null against an API older than Tina
   * migration `0057`, which is different from "all five are zero" and is
   * rendered as such.
   */
  statusMix: StatusMix | null;
  /**
   * The single biggest reason this stage is not `ok`, for the one-line summary
   * next to the failure rate. Null when `statusMix` is null or all-zero.
   */
  dominantOther: { status: NonOkStatus; count: number; share: number } | null;
  /**
   * Execution-weighted mean of the per-(day, prompt-version) p50s — an
   * **approximation**, and a coarse one. Percentiles do not recombine: averaging
   * them pulls the result toward the middle of the per-group values, so a stage
   * whose tail lives in a few busy groups reads far too fast, and a stage whose
   * slow groups are small reads too slow. Measured against the true pooled
   * `percentile_cont` over prod's seven days to 2026-08-28, this figure was
   * **-63%** for `screening_dedup`, **-45%** for `enrich` and **+862%** for
   * `fetch_check`, and it does not even rank stages in the right order
   * (`fetch_check` is really the fastest stage in the fleet; this ranks it
   * 12th of 22). Render it as `≈`, never as "the p50", and never use it to
   * decide which stage to optimise — open the stage and measure.
   *
   * The axis being averaged is `(day, prompt_version_id)`, not `day`: a stage
   * mid-rollout blends two prompt versions' latencies into one number.
   */
  p50Ms: number | null;
  /** Same caveat as `p50Ms`. */
  p95Ms: number | null;
  /**
   * The day with the highest failure rate — where to start looking. Null when
   * the stage did not fail once in the window, which is the answer: there is no
   * bad day to open. This used to be the day with the *fewest* `ok`s, which for
   * `venue_resolution` picked whichever day had the most cache hits.
   */
  worstDay: { day: string; failRate: number; failCount: number } | null;
  /** Distinct days the stage ran in the window. */
  days: number;
  /** Distinct prompt versions seen — >1 means a rollout is in flight. */
  promptVersions: number;
}

interface DayAcc {
  executions: number;
  okCount: number;
  failCount: number;
}

interface Acc {
  stage: string;
  executions: number;
  okCount: number;
  failCount: number;
  /** Every failed attempt, fixed or not — what `otherCount` subtracts. */
  rawFailCount: number;
  recoveredCount: number;
  mix: StatusMix;
  /** True once any row carried per-status counts, i.e. the API is >= `0057`. */
  sawMix: boolean;
  p50Weighted: number;
  p50Weight: number;
  p95Weighted: number;
  p95Weight: number;
  days: Map<string, DayAcc>;
  promptVersions: Set<string>;
}

/** `0057`'s wire names for the five non-`ok`, non-failure counts. */
const MIX_FIELDS: Record<NonOkStatus, keyof StagePerfDailyRow> = {
  inherited: "inherited_count",
  skipped: "skipped_count",
  duplicate: "duplicate_count",
  hitl: "hitl_count",
  deferred: "deferred_count",
};

function emptyMix(): StatusMix {
  return { inherited: 0, skipped: 0, duplicate: 0, hitl: 0, deferred: 0 };
}

export interface StageHealthOptions {
  /** Count the failures a later retry fixed (default false). */
  includeRecovered?: boolean;
}

export function aggregateStageHealth(
  rows: StagePerfDailyRow[],
  { includeRecovered = false }: StageHealthOptions = {},
): StageHealthRow[] {
  const byStage = new Map<string, Acc>();

  for (const r of rows) {
    let acc = byStage.get(r.stage);
    if (!acc) {
      acc = {
        stage: r.stage,
        executions: 0,
        okCount: 0,
        failCount: 0,
        rawFailCount: 0,
        recoveredCount: 0,
        mix: emptyMix(),
        sawMix: false,
        p50Weighted: 0,
        p50Weight: 0,
        p95Weighted: 0,
        p95Weight: 0,
        days: new Map(),
        promptVersions: new Set(),
      };
      byStage.set(r.stage, acc);
    }
    const execs = r.executions ?? 0;
    const rawFail = r.fail_count ?? 0;
    // Clamped so a malformed row can never make the failure count negative.
    const recovered = Math.min(rawFail, r.recovered_fail_count ?? 0);
    const fail = includeRecovered ? rawFail : rawFail - recovered;
    acc.executions += execs;
    acc.okCount += r.ok_count ?? 0;
    acc.failCount += fail;
    acc.rawFailCount += rawFail;
    acc.recoveredCount += recovered;

    for (const status of NON_OK_STATUSES) {
      const value = r[MIX_FIELDS[status]];
      if (typeof value === "number") {
        acc.mix[status] += value;
        acc.sawMix = true;
      }
    }

    if (r.p50_ms != null && execs > 0) {
      acc.p50Weighted += r.p50_ms * execs;
      acc.p50Weight += execs;
    }
    if (r.p95_ms != null && execs > 0) {
      acc.p95Weighted += r.p95_ms * execs;
      acc.p95Weight += execs;
    }
    if (r.prompt_version_id) acc.promptVersions.add(r.prompt_version_id);

    let day = acc.days.get(r.day);
    if (!day) {
      day = { executions: 0, okCount: 0, failCount: 0 };
      acc.days.set(r.day, day);
    }
    day.executions += execs;
    day.okCount += r.ok_count ?? 0;
    day.failCount += fail;
  }

  const out: StageHealthRow[] = [];
  for (const acc of byStage.values()) {
    // The worst day is the one that failed most, and only if it failed at all.
    let worstDay: StageHealthRow["worstDay"] = null;
    for (const [day, d] of acc.days) {
      if (d.executions === 0 || d.failCount === 0) continue;
      const failRate = d.failCount / d.executions;
      if (!worstDay || failRate > worstDay.failRate) {
        worstDay = { day, failRate, failCount: d.failCount };
      }
    }

    const statusMix = acc.sawMix ? acc.mix : null;
    let dominantOther: StageHealthRow["dominantOther"] = null;
    if (statusMix && acc.executions > 0) {
      for (const status of NON_OK_STATUSES) {
        const count = statusMix[status];
        if (count > 0 && (!dominantOther || count > dominantOther.count)) {
          dominantOther = { status, count, share: count / acc.executions };
        }
      }
    }

    out.push({
      stage: acc.stage,
      stageClass: isEnrichmentStage(acc.stage) ? "enrichment" : "scrape",
      executions: acc.executions,
      okCount: acc.okCount,
      failCount: acc.failCount,
      recoveredCount: acc.recoveredCount,
      failRate: acc.executions > 0 ? acc.failCount / acc.executions : null,
      okRate: acc.executions > 0 ? acc.okCount / acc.executions : null,
      otherCount: Math.max(0, acc.executions - acc.okCount - acc.rawFailCount),
      statusMix,
      dominantOther,
      p50Ms: acc.p50Weight > 0 ? acc.p50Weighted / acc.p50Weight : null,
      p95Ms: acc.p95Weight > 0 ? acc.p95Weighted / acc.p95Weight : null,
      worstDay,
      days: acc.days.size,
      promptVersions: acc.promptVersions.size,
    });
  }

  // Failures first (that is what the page is for), then by volume, then name —
  // so the order is stable across refreshes.
  return out.sort(
    (a, b) =>
      b.failCount - a.failCount ||
      b.executions - a.executions ||
      a.stage.localeCompare(b.stage),
  );
}

/** Human duration for a millisecond figure that may be null. */
export function formatMs(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return "—";
  if (value < 1000) return `${Math.round(value)} ms`;
  if (value < 60_000) return `${(value / 1000).toFixed(1)} s`;
  return `${(value / 60_000).toFixed(1)} min`;
}

/** `0.9873` → `"98.7%"`; null → `"—"`. */
export function formatRate(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return `${(value * 100).toFixed(1)}%`;
}

/**
 * One-line "why is this stage not ok" for the row summary — e.g.
 * `"80.0% inherited"`. Empty string when there is nothing to explain (the stage
 * is entirely `ok`); against a pre-`0057` API it can only say how many, not
 * which.
 */
export function formatDominantOther(row: StageHealthRow): string {
  if (!row.dominantOther) {
    if (row.otherCount === 0) return "";
    return `${row.otherCount.toLocaleString()} not ok`;
  }
  return `${formatRate(row.dominantOther.share)} ${row.dominantOther.status}`;
}
